import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  demoAccounts,
  findAccount,
  findById,
  canSee,
  type DemoAccount,
} from '@/data/demo-accounts';

// localStorage — sesja persists pomiedzy tabami i po zamknieciu przegladarki/PWA.
// Wylogowanie wymaga explicit klikniecia w UserMenu lub Settings.
// Browser back/swipe i PWA backgrounding nie wyrzucaja z sesji.
const STORAGE_KEY = 'moredeal.session';

export const useAuthStore = defineStore('auth', () => {
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
  const user = ref<DemoAccount | null>(stored ? findById(stored) : null);

  const isAuthenticated = computed(() => user.value != null);

  const scopeLabel = computed(() => {
    if (!user.value) return '';
    const s = user.value.scope;
    switch (s.kind) {
      case 'dealer':
        return `Caly dealer: ${s.value}`;
      case 'brand':
        return `Marka: ${s.value}`;
      case 'branch':
        return `Oddzial: ${s.value}`;
    }
  });

  function persist() {
    if (typeof localStorage === 'undefined') return;
    if (user.value) localStorage.setItem(STORAGE_KEY, user.value.id);
    else localStorage.removeItem(STORAGE_KEY);
  }

  function signIn(
    email: string,
    password: string,
  ): { ok: true; user: DemoAccount } | { ok: false; error: string } {
    const found = findAccount(email, password);
    if (!found) return { ok: false, error: 'Niepoprawny email lub haslo' };
    user.value = found;
    persist();
    return { ok: true, user: found };
  }

  function signInAs(accountId: string): boolean {
    const found = findById(accountId);
    if (!found) return false;
    user.value = found;
    persist();
    return true;
  }

  function signOut() {
    user.value = null;
    persist();
  }

  function canSeeItem(item: { brand?: string; branch?: string; advisorName?: string }): boolean {
    if (!user.value) return false;
    return canSee(user.value, item);
  }

  return {
    user,
    isAuthenticated,
    scopeLabel,
    accounts: demoAccounts,
    signIn,
    signInAs,
    signOut,
    canSeeItem,
  };
});
